/**
 * Contact/consultant form → chat bridge.
 *
 * The form blob is sent as a normal chat message to the ob-agent website
 * assistant, whose escalate_to_human tool notifies the team (Lark) with the
 * visitor's contact details — the reply the visitor sees in the chat is the
 * model's own acknowledgement. A localized fallback keeps the chat usable
 * when the model finishes without reply text.
 */

import {
  IFormSubmissionParams,
  IFormSubmissionResult,
} from '@/interfaces/forms';
import { IAgentForm } from '@/interfaces/obAgent';
import { identifyUser, setTag } from '@/lib/analytics';
import { respondToForm, streamChat } from '@/lib/obAgentApi';

function fallbackReply(
  language: IFormSubmissionParams['language'],
  name: string,
  formType: IFormSubmissionParams['formType']
): string {
  if (language === 'en') {
    return formType === 'consultant'
      ? `Thanks ${name}! We've received your consultation request. Our team will contact you shortly to arrange the details.`
      : `Thanks ${name}! We've received your message. Our team will get back to you shortly.`;
  }
  return formType === 'consultant'
    ? `謝謝 ${name}！我們已收到您的諮詢申請，團隊會盡快與您聯絡並安排後續。`
    : `謝謝 ${name}！我們已收到您的訊息，團隊會盡快回覆您。`;
}

function answersForAgentForm(
  form: IAgentForm,
  submitted: Record<string, string | string[]>
): Record<string, string | boolean | string[]> {
  const usable = (
    value: string | string[] | undefined
  ): value is string | string[] =>
    Array.isArray(value) ? value.length > 0 : Boolean(value?.trim());
  const pick = (...keys: string[]) => {
    const key = keys.find(candidate => usable(submitted[candidate]));
    return key ? submitted[key] : undefined;
  };

  const answers: Record<string, string | boolean | string[]> = {};
  for (const question of form.questions) {
    const direct = submitted[question.id];
    if (usable(direct)) {
      answers[question.id] = direct;
      continue;
    }

    const field = `${question.id} ${question.question}`.toLowerCase();
    let answer: string | string[] | undefined;
    if (
      /business.?status|opening.?status|營業狀態|業務狀態|開業狀態/.test(field)
    ) {
      answer = pick('business_status');
    } else if (
      /business.?name|company.?name|brand.?name|商家名稱|公司名稱|企業名稱|品牌名稱|業務名稱/.test(
        field
      )
    ) {
      answer = pick('business_name', 'company');
    } else if (/e-?mail|電子郵件|電郵|郵箱/.test(field)) {
      answer = pick('email');
    } else if (
      /phone|mobile|contact.?number|電話|手機|聯絡號碼|聯絡電話/.test(field)
    ) {
      answer = pick('phone');
    } else if (/hear|referr|得知|知道.*omcean/.test(field)) {
      answer = pick('hear_about');
    } else if (/line.?id|line.?帳號|line.?账号/.test(field)) {
      answer = pick('line_id');
    } else if (/business.?type|業務類型|商家類型/.test(field)) {
      answer = pick('business_type');
    } else if (/interest|想了解|感興趣/.test(field)) {
      answer = pick('interests');
    } else if (/subject|主題|主题/.test(field)) {
      answer = pick('subject');
    } else if (/message|details|需求|訊息|消息|留言/.test(field)) {
      answer = pick('message');
    } else if (/name|姓名|稱呼/.test(field)) {
      answer = pick('name');
    }

    if (usable(answer)) answers[question.id] = answer;
  }
  return answers;
}

export async function submitFormToChat(
  params: IFormSubmissionParams
): Promise<IFormSubmissionResult> {
  const { language, messageText, name, email, formType, formAnswers } = params;

  try {
    if (formType === 'consultant' && !name.trim()) {
      throw new Error(
        'Please enter your name before submitting the consultation request.'
      );
    }
    // Identify user in analytics (both GA and Clarity)
    identifyUser(email, { name, email });
    setTag('form_type', formType);

    // Stream the assistant's acknowledgement; collect the final text only
    // (the widget is usually closed during a form submit — no live render).
    let replyText = '';
    let delivered = false;
    let streamError: string | undefined;
    await streamChat(
      messageText,
      {
        onDelta: () => {},
        onDone: fullText => {
          replyText = fullText;
        },
        onError: message => {
          streamError = message;
          replyText = '';
        },
        onToolResult: result => {
          const expectedTool =
            formType === 'consultant'
              ? 'book_consultation'
              : 'escalate_to_human';
          if (
            result.tool === expectedTool &&
            result.ok &&
            result.delivered === true
          )
            delivered = true;
        },
        onForm: async form => {
          const answers = answersForAgentForm(form, formAnswers);
          const missing = form.questions.filter(
            question =>
              question.required &&
              (answers[question.id] == null ||
                answers[question.id] === '' ||
                (Array.isArray(answers[question.id]) &&
                  (answers[question.id] as string[]).length === 0))
          );
          if (missing.length) {
            await respondToForm(form.requestId, {
              answers: {},
              cancelled: true,
            });
            throw new Error(
              'Please complete the required consultation details.'
            );
          }
          const resolved = await respondToForm(form.requestId, {
            answers,
          });
          if (!resolved) {
            throw new Error('Could not answer the agent form.');
          }
        },
      },
      typeof window !== 'undefined' ? window.location.pathname : undefined,
      language
    );

    if (!delivered) {
      throw new Error(
        streamError ||
          'We could not confirm delivery of your request. Please check your connection before trying again.'
      );
    }
    if (!replyText.trim()) {
      replyText = fallbackReply(language, name, formType);
    }

    // The turn is persisted server-side (user form message + reply). Tell the
    // ChatWidget to pull the new rows and surface them as unread; the widget
    // keeps no local transcript.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('chatTranscriptChanged', {
          detail: { fallbackReply: replyText },
        })
      );
    }

    return { success: true };
  } catch (error) {
    console.error('Form submission failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
